//
// PLEASE READ
// In an effort to gradually clean up this monolithic file, we have
// started to move API calls to the ./api/... folder, in a respective area.
//
// Please read ./api/index.ts
//

import { Patient } from './api';
import { ApiResponse, ApisauceInstance, create } from 'apisauce';
import { AxiosResponse, CancelToken } from 'axios';
import moment from 'moment-timezone';
import { toastr } from 'react-redux-toastr';
import {
  AgencyAddressesResponse,
  AgencyData,
  AgencyIdentity,
  AgencyMetadata,
  AgencyPaymentsResponse,
  AgencyResponse,
  ConciergeAgenciesResponse,
  ConciergeAgentsResponse,
} from './redux/concierge/types';
import { Area } from './redux/market-map/types';
import { BillingProvider, Claim, ClaimEvaluationRequest, ClaimEvaluationResponse, ClaimsServerResponse, Payer } from './redux/claim/types';
import { Account, AccountResponse } from './api/accounts.types';
import { Address, CoverageResponse, CreateAddressRequest, EditAddressRequest, EditAddressResponse } from './api/address.types';
import { ServiceOffering } from './api/service-offerings-types';
import { TimeSlotData } from './api/timeslots.types';
import { FeeSchedule } from './redux/fee-schedule/types';
import { OnSite } from './redux/on-site-manager/on-sites/types';
import { Partner, PartnerAddress, PartnerLogo } from './redux/on-site-manager/partners/types';
import {
  CreateFileResponse,
  CreateFormRequest,
  CreateFormResponse,
  PatchFileRequest,
  PatientFileData,
  PatientFileGrantsResponse,
  PatientFileResponse,
  PatientFilesDataResponse,
} from './redux/patient-files/types';
import {
  CreatePatientRequest,
  CreatePatientResponse,
  EditPatientRequest,
  EditPatientResponse,
  InsurancePlanDocument,
  LineType,
  UpdatePatientTagsRequest,
} from './redux/patient/types';
import { SearchPatient } from './redux/patients/types';
import {
  Break,
  PCPFormData,
  PCPFormRequest,
  PCPStatusChangeRequest,
  PreferredDoctorRequest,
  ProviderBreakdown,
  ProviderPairingRule,
  ScheduleResponse,
  SearchProvider,
} from './redux/providers/types';
import { DailyShiftSchedule, Shift } from './redux/scheduling/types';
import { BaseResponse, ServerResponse } from './redux/types';
import {
  CancelVisitResponse,
  WebVisit,
  TimeSlot,
  Visit,
  VisitAdminQueryResponse,
  VisitConfigServerResponse,
  VisitLogEntry,
  VisitLogResponse,
  ConfirmationStateType,
  CancelReasons,
} from './redux/visits/types';
import { StatusOptions } from './types';
import { toISODateString } from './util/Date';
import { getAccessToken } from './okta';

declare const healConfig: any;

export interface CreateFileParams {
  patientId: string;
  contentType: string; // .pdf, .png
  releaseToPatient: boolean;
  extension?: string;
  documentType?: string;
  visitCode?: string;
}

export class HealAPI {
  public static fetchVisits(
    markets?: string[],
    limit = 100,
    status?: string,
    startdateStr?: string,
    endDateStr?: string,
    includeCampaign = false
  ): Promise<ApiResponse<VisitAdminQueryResponse>> {
    const startDate = (startdateStr && new Date(startdateStr)) || new Date();
    const endDate = (endDateStr && new Date(endDateStr)) || new Date(startDate);
    const filteredMarkets = (markets || []).filter((m) => m !== 'ALL');
    const params = {
      markets: (filteredMarkets.length > 0 && filteredMarkets.join(',')) || undefined,
      limit,
      status: status,
      include_campaigns: includeCampaign,
      start_date: startDate.setHours(0, 0, 0, 0),
      end_date: endDate.setHours(23, 59, 59, 999),
    };
    if (includeCampaign) {
      delete params.markets;
      delete params.status;
    }
    return HealAPI.createApi().then((api) => api.get<VisitAdminQueryResponse>('/admin/visits/admin/query', params));
  }

  public static fetchDashboardOverview(date: string): Promise<BaseResponse<any[]>> {
    return HealAPI.createApi()
      .then((api) => api.get<any>('/admin/dashboard/visits', { date: toISODateString(date) }))
      .then((r) => r.data!);
  }

  public static fetchVisit(visitCode: string): Promise<ApiResponse<Visit>> {
    return HealAPI.createApi().then((api) => api.get<Visit>(`/admin/visits/admin/one/${visitCode}`));
  }

  public static fetchVisitLogs(visitCode: string): Promise<ApiResponse<VisitLogResponse>> {
    return HealAPI.createApi().then((api) => api.get<VisitLogResponse>(`/admin/visit/${visitCode}/logs`, { maxCount: 200 }));
  }

  public static fetchRecommendedTimeslotsForProvider(
    date_start: string,
    date_end: string,
    latitude: number,
    longitude: number,
    provider_id?: string,
    service_code?: string
  ): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.get<any>('/eta/smart_ana', { date_start, date_end, latitude, longitude, provider_id, service_code }));
  }

  public static fetchSchedule(date: string, market_group: string): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.get<any>('/eta/eta_pred', { query_type: 'schedule', market_group, date }));
  }

  public static fetchEtaScoreBreakdown(visitCode: string): Promise<ApiResponse<ProviderBreakdown[]>> {
    return HealAPI.createEtaApi().then((api) => api.get<ProviderBreakdown[]>(`/score_breakdown?visit_code=${visitCode}`));
  }

  public static insertLog(visitCode: string, body: string): Promise<ApiResponse<VisitLogEntry>> {
    return HealAPI.createApi(false).then((api) => api.post<VisitLogEntry>(`/admin/visit/${visitCode}/log`, { body }));
  }

  public static startVisit(visitCode: string, time: Date): Promise<ApiResponse<Visit>> {
    return HealAPI.createApi(true).then((api) => api.post<Visit>(`/admin/visits/${visitCode}/start_visit`, { statusTime: time.getTime() }));
  }

  public static endVisit(visitCode: string, time: Date): Promise<ApiResponse<Visit>> {
    return HealAPI.createApi(true).then((api) => api.post<Visit>(`/admin/visits/${visitCode}/end_visit`, { statusTime: time.getTime() }));
  }

  public static checkOutVisit(visitCode: string, time: Date): Promise<ApiResponse<Visit>> {
    return HealAPI.createApi(true).then((api) => api.post<Visit>(`/admin/visits/${visitCode}/checkout`, { statusTime: time.getTime() }));
  }

  public static cancelVisit(visitCode: string, approved: boolean, cancelReasonId: string, note: string, rebooking: string, notifyPatient?: boolean) {
    return HealAPI.createApi(false).then((api) =>
      api.post<CancelVisitResponse>(`/admin/visits/${visitCode}/cancel`, {
        approved,
        cancelReasonId,
        note,
        rebooking,
        notifyPatient,
      })
    );
  }

  public static cancelAllFutureVisits(patientId: string, cancelReasonId: string, note: string) {
    return HealAPI.createApi(false).then((api) =>
      api.post<VisitAdminQueryResponse>(`/admin/patient/${patientId}/cancelFutureVisits`, {
        cancelReasonId,
        note,
      })
    );
  }

  public static setProviderOnCall(providerId: string): Promise<ApiResponse<any>> {
    // TODO: this api requires a location to put the doctor on-call -- using the headquarters location.
    const params = {
      lat: 34.0620125,
      lon: -118.41435920000004,
      zipcode: '90067',
    };
    return HealAPI.createApi(false).then((api) => api.post<any>(`/admin/providers-status/${providerId}/on_call`, params));
  }

  public static setProviderOffCall(providerId: string): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.post<any>(`/admin/providers-status/${providerId}/unavailable`, {}));
  }

  public static changeProvider(
    visitCode: string,
    overridePCPProvider: boolean,
    patientAffected?: boolean,
    providerId?: string,
    assistantId?: string,
    timeSlotId?: string,
    time?: number
  ) {
    const params: any = { providerId, timeSlotId, time, patientAffected, overridePCPProvider };
    if (assistantId) {
      params.assistantId = assistantId;
    }
    return HealAPI.createApi(false).then((api) => api.post<WebVisit>(`/admin/visits/${visitCode}/changeProvider`, params));
  }

  public static updateTime(visitCode: string, timeSlotId: string): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.post<any>(`/admin/onsite/visit/${visitCode}/reschedule`, { timeSlotId }));
  }

  public static createBreak(doctorId: string, startTime: number, endTime: number, type: string, MAId?: string): Promise<ApiResponse<Break>> {
    return HealAPI.createApi(false).then((api) =>
      api.post<Break>(`/admin/breaks`, {
        doctorId: doctorId,
        assistantId: MAId,
        startTime: startTime,
        endTime: endTime,
        type: type,
      })
    );
  }

  public static editBreak(id: string, doctorId: string, startTime: number, endTime: number, type: string, MAId?: string): Promise<ApiResponse<Break>> {
    return HealAPI.createApi(false).then((api) =>
      api.put<Break>(`/admin/breaks`, {
        doctorId: doctorId,
        assistantId: MAId,
        startTime: startTime,
        endTime: endTime,
        type: type,
        id: id,
      })
    );
  }

  public static deleteBreak(breakId: string): Promise<ApiResponse<void>> {
    return HealAPI.createApi(false).then((api) => api.delete<void>(`/admin/breaks/${breakId}`));
  }

  public static fetchAllOnSites(): Promise<ApiResponse<OnSite[]>> {
    return HealAPI.createApi(false).then((api) => api.get<OnSite[]>('/admin/onsite'));
  }

  public static createOnSiteEvent(
    name: string,
    partnerAddressId: string,
    partnerId: string,
    startDateTime: string,
    endDateTime: string,
    appointmentsRequested: number,
    serviceCode: string,
    leadDoctorId: string,
    price: string,
    info?: string,
    room?: string,
    breakData?: { startTime?: string; endTime?: string }
  ): Promise<ApiResponse<any>> {
    const params = {
      name,
      partnerId,
      partnerAddressId,
      startDateTime,
      endDateTime,
      appointmentsRequested,
      serviceCode,
      leadDoctorId,
      price,
      info: info || '',
      room: room || '',
      break: breakData || null,
    };
    return HealAPI.createApi(false).then((api) => api.post<OnSite>(`/admin/onsite`, params));
  }

  public static editOnSiteEvent(
    onSiteId: string,
    name: string,
    startTime: string,
    endTime: string,
    appointmentsRequested: number,
    room?: string,
    info?: string,
    leadDoctorId?: string,
    price?: string
  ): Promise<ApiResponse<any>> {
    const params = {
      name,
      startTime,
      endTime,
      appointmentsRequested,
      leadDoctorId,
      price,
      info: info || '',
      room: room || '',
    };
    return HealAPI.createApi(false).then((api) => api.put<OnSite>(`/admin/onsite/${onSiteId}`, params));
  }

  public static sendOnSiteEmail(onSiteId: string, isPromo: boolean, isCashPay: boolean): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.post<any>(`/admin/onsite/${onSiteId}/email`, { isPromo, isCashPay }));
  }

  public static deleteOnSiteEvent(onSiteId: string): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.delete<any>(`/admin/onsite/${onSiteId}`));
  }

  public static updateInsurance(visitCode: string, payerId: string, memberId: string, groupId: string, show: boolean): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) =>
      api.post<any>(`/admin/visits/${visitCode}/update_insurance`, {
        payerId,
        memberId,
        groupId,
        show,
      })
    );
  }

  public static updateVisitPatient(visitCode: string, patientId: string): Promise<ApiResponse<Visit>> {
    return HealAPI.createApi(false).then((api) => api.put<Visit>(`/admin/visits/${visitCode}/replacePatient`, { patientId: patientId }));
  }

  public static updateField(visitCode: string, apiEndpoint: string, requestParams: any): Promise<ApiResponse<ServerResponse>> {
    return HealAPI.createApi().then((api) => api.post<any>(`/admin/visits/${visitCode}/${apiEndpoint}`, requestParams));
  }

  public static fetchAvailableTimeSlots(visitCode: string): Promise<ApiResponse<TimeSlot[]>> {
    return HealAPI.createApi().then((api) => api.get<TimeSlot[]>(`/admin/visits/${visitCode}/available_time_slots`));
  }

  public static fetchAvailableOnSiteTimeSlots(visitCode: string): Promise<ApiResponse<TimeSlot[]>> {
    return HealAPI.createApi().then((api) => api.get<TimeSlot[]>(`/admin/onsite/visit/${visitCode}/timeslots`));
  }

  public static fetchAllSchedules(market: string, dateStr?: string): Promise<ApiResponse<ScheduleResponse>> {
    const date = (dateStr && new Date(dateStr)) || new Date();

    const params = {
      market: market,
      start_date: date.setHours(0, 0, 0, 0),
      end_date: date.setHours(23, 59, 59, 999),
    };
    return HealAPI.createApi().then((api) => api.get<ScheduleResponse>('/admin/providers/all/schedule', params));
  }

  public static fetchVisitConfig(includeAllPayers = false): Promise<ApiResponse<VisitConfigServerResponse>> {
    return HealAPI.createApi().then((api) => api.get<VisitConfigServerResponse>('/admin/visits/config?include_all_payers=' + includeAllPayers));
  }

  public static async fetchCancelReasons() {
    return (await HealAPI.createApi()).get<BaseResponse<CancelReasons>>('/admin/visits/cancel-reasons');
  }

  public static sendReferrals(specialistIds: string[]): Promise<string> {
    if (specialistIds.length === 5) {
      return Promise.reject('Error Concept: Failed to send faxes.');
    }
    return Promise.resolve(`Successfully sent faxes to these specialistIds: ${specialistIds.join(', ')}`);
  }

  public static searchPatient(keyword: string): Promise<ApiResponse<SearchPatient[]>> {
    return HealAPI.createApi().then((api) => api.get<SearchPatient[]>('/admin/typeahead/patient', { keyword }));
  }

  public static fetchAgencies() {
    return HealAPI.createApi().then((api) => api.get<ConciergeAgenciesResponse>('/admin/concierge-agency'));
  }

  public static fetchAgency(conciergeAgencyId: string): Promise<ApiResponse<AgencyResponse>> {
    return HealAPI.createApi().then((api) => api.get<AgencyResponse>(`/admin/concierge-agency/${conciergeAgencyId}`));
  }

  public static deleteAgency(conciergeAgencyId: string): Promise<ApiResponse<string>> {
    return HealAPI.createApi().then((api) => api.delete<string>(`/admin/concierge-agency/${conciergeAgencyId}`));
  }

  public static updateAgency(agency: AgencyData) {
    return HealAPI.createApi(false).then((api) => api.put<AgencyResponse>(`/admin/concierge-agency/${agency.id}`, agency));
  }

  public static fetchAgents(agencyId: string): Promise<ApiResponse<ConciergeAgentsResponse>> {
    return HealAPI.createApi().then((api) => api.get<ConciergeAgentsResponse>(`/admin/concierge-agency/${agencyId}/users`));
  }

  public static removeConciergeAgent(conciergeAgencyId: string, accountId: string): Promise<ApiResponse<string>> {
    return HealAPI.createApi().then((api) => api.delete<string>(`/admin/concierge-agency/${conciergeAgencyId}/users/${accountId}`));
  }

  public static fetchAgencyAddresses(conciergeAgencyId: string): Promise<ApiResponse<AgencyAddressesResponse>> {
    return HealAPI.createApi().then((api) => api.get<AgencyAddressesResponse>(`/admin/concierge-agency/${conciergeAgencyId}/address`));
  }

  public static removeAgencyAddress(conciergeAgencyId: string, addressId: string): Promise<ApiResponse<string>> {
    return HealAPI.createApi().then((api) => api.delete<string>(`/admin/concierge-agency/${conciergeAgencyId}/address/${addressId}`));
  }

  public static fetchAgencyPayments(conciergeAgencyId: string): Promise<ApiResponse<AgencyPaymentsResponse>> {
    return HealAPI.createApi().then((api) => api.get<AgencyPaymentsResponse>(`/admin/concierge-agency/${conciergeAgencyId}/payment`));
  }

  public static removeAgencyPayment(conciergeAgencyId: string, paymentId: string): Promise<ApiResponse<string>> {
    return HealAPI.createApi().then((api) => api.delete<string>(`/admin/concierge-agency/${conciergeAgencyId}/payment/${paymentId}`));
  }

  public static createAgency(group: AgencyMetadata): Promise<ApiResponse<any>> {
    return HealAPI.createApi().then((api) => api.post<any>('/admin/concierge-agency', group));
  }

  public static addAgentToAgency(conciergeAgencyId: string, userAccountIds: string[]): Promise<ApiResponse<any>> {
    return HealAPI.createApi().then((api) =>
      api.post<any>(`/admin/concierge-agency/${conciergeAgencyId}/users`, {
        userAccountIds,
      })
    );
  }

  public static addAddressToAgency(conciergeAgencyId: string, address: CreateAddressRequest): Promise<ApiResponse<any>> {
    return HealAPI.createApi().then((api) => api.post<any>(`/admin/concierge-agency/${conciergeAgencyId}/address`, address));
  }

  public static searchAccount(keyword: string): Promise<ApiResponse<Account[]>> {
    return HealAPI.createApi().then((api) => api.get<Account[]>('/admin/person/typeahead', { keyword }));
  }

  public static searchProvider(keyword: string): Promise<ApiResponse<SearchProvider[]>> {
    return HealAPI.createApi().then((api) => api.get<SearchProvider[]>('/admin/typeahead/provider?include=markets,pdmarkets', { keyword }));
  }

  public static checkAddressCoverage(latitude: number, longitude: number): Promise<BaseResponse<CoverageResponse>> {
    return HealAPI.createApi()
      .then((api) => api.get<BaseResponse<CoverageResponse>>(`/admin/check_coverage?latitude=${latitude}&longitude=${longitude}`))
      .then((r) => r.data!);
  }

  public static checkZipcodeCoverage(zipcode: string): Promise<BaseResponse<CoverageResponse>> {
    return HealAPI.createApi()
      .then((api) => api.get<BaseResponse<CoverageResponse>>(`/admin/check_coverage?zipcode=${zipcode}`))
      .then((r) => r.data!);
  }

  public static fetchAreasByIntersection(query: any, cancelToken?: CancelToken): Promise<ApiResponse<Area[]>> {
    return HealAPI.createAtlasApi().then((api) =>
      api.post<Area[]>(`/v1/area/intersect`, query, {
        cancelToken: cancelToken,
      })
    );
  }

  public static patchArea(area: Partial<Area>): Promise<ApiResponse<Area>> {
    const id = area.id;
    return HealAPI.createAtlasApi().then((api) => api.patch<Area>(`/v1/area/${id}`, area));
  }

  public static deleteArea(area: Area): Promise<ApiResponse<Area>> {
    const id = area.id;
    return HealAPI.createAtlasApi().then((api) => api.delete<Area>(`/v1/area/${id}`));
  }

  public static createArea(area: Area): Promise<ApiResponse<Area>> {
    return HealAPI.createAtlasApi().then((api) => api.post<Area>(`/v1/area/`, area));
  }

  public static fetchClaimsByVisitCode(visitCode: string, latestRevisionsOnly?: boolean) {
    return HealAPI.createApi().then((api) => api.get<Claim[]>(`/insurance/v1/claim/external_reference/${visitCode}${latestRevisionsOnly ? `?latestRevisionsOnly=true` : ''}`));
  }

  public static fetchFeeSchedulesByPayerAndBillingProvider(payerId: string, billingProviderId: number): Promise<ApiResponse<FeeSchedule[]>> {
    return HealAPI.createApi().then((api) => api.get<FeeSchedule[]>(`/insurance/v1/fee_schedule/billingProvider/${billingProviderId}/payer/${payerId}`));
  }

  public static updateFeeSchedule(item: FeeSchedule): Promise<ApiResponse<FeeSchedule>> {
    return HealAPI.createApi().then((api) => api.put<FeeSchedule>(`/insurance/v1/fee_schedule/${item.id}`, item));
  }

  public static createFeeSchedule(item: FeeSchedule): Promise<ApiResponse<FeeSchedule>> {
    return HealAPI.createApi().then((api) => api.post<FeeSchedule>(`/insurance/v1/fee_schedule/`, item));
  }

  public static fetchFeeSchedulesByPayerAndBillingProviderAndCpt(payerId: string, billingProviderId: number, cpt: string): Promise<ApiResponse<FeeSchedule[]>> {
    return HealAPI.createApi().then((api) => api.get<FeeSchedule[]>(`/insurance/v1/fee_schedule/billingProvider/${billingProviderId}/payer/${payerId}/cpt/${cpt}`));
  }

  public static fetchClaims(startISO?: string, endISO?: string, states?: string[], searchQuery?: string): Promise<ApiResponse<ClaimsServerResponse>> {
    const params: any = {
      size: 2000,
      start: startISO ? moment(startISO).format('YYYY-MM-DDT00:00:00.000') + 'Z' : undefined,
      end: endISO ? moment(endISO).format('YYYY-MM-DDT23:59:59.999') + 'Z' : undefined,
    };
    if (states && states.length > 0) {
      params.states = states.join(',');
    }
    if (searchQuery) {
      params.q = searchQuery;
    }

    return HealAPI.createApi().then((api) => api.get<ClaimsServerResponse>('/insurance/v1/claim', params));
  }

  public static evaluateForClaimGeneration(source: string, data: ClaimEvaluationRequest): Promise<ApiResponse<ClaimEvaluationResponse>> {
    return HealAPI.createOpsApi().then((api) => api.post<ClaimEvaluationResponse>(`/vony/v1/visit_source/${source}/evaluate`, data));
  }

  public static submitClaim(claimId: string): Promise<ApiResponse<Claim>> {
    return HealAPI.createApi().then((api) => api.post<Claim>(`/insurance/v1/claim/${claimId}/submit`));
  }

  public static refreshClaim(claimId: string): Promise<ApiResponse<Claim>> {
    return HealAPI.createApi().then((api) => api.post<Claim>(`/insurance/v1/claim/${claimId}/refresh`));
  }

  public static resetClaim(claimId: string): Promise<ApiResponse<Claim>> {
    return HealAPI.createApi().then((api) => api.post<Claim>(`/insurance/v1/claim/${claimId}/reset`));
  }

  public static fetchPayers(): Promise<ApiResponse<Payer[]>> {
    return HealAPI.createApi().then((api) => api.get<Payer[]>(`/insurance/v1/payer/`));
  }

  public static fetchBillingProviders(): Promise<ApiResponse<BillingProvider[]>> {
    return HealAPI.createApi().then((api) => api.get<BillingProvider[]>(`/insurance/v1/billing_provider/`));
  }

  public static fetchClaimByID(claimID: string): Promise<ApiResponse<Claim>> {
    return HealAPI.createApi().then((api) => api.get<Claim>(`/insurance/v1/claim/${claimID}`));
  }

  public static updateClaimDetails(claim: Claim): Promise<ApiResponse<Claim>> {
    return HealAPI.createApi().then((api) => api.put<Claim>(`/insurance/v1/claim/${claim.id}`, claim));
  }

  public static fetchPartners(): Promise<ApiResponse<Partner[]>> {
    return HealAPI.createApi().then((api) => api.get<Partner[]>('/admin/partner'));
  }

  public static fetchPartner(partnerId: string): Promise<ApiResponse<Partner>> {
    return HealAPI.createApi().then((api) => api.get<Partner>(`/admin/partner/${partnerId}`));
  }

  public static createPartner(newPartner: Partner): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.post<Partner>('/admin/partner', newPartner));
  }

  public static editPartner(partner: Partner): Promise<ApiResponse<any>> {
    return HealAPI.createApi(false).then((api) => api.put<Partner>(`/admin/partner/${partner.id}`, partner));
  }

  public static createPartnerAddress(partnerId: string, address: PartnerAddress): Promise<ApiResponse<PartnerAddress>> {
    return HealAPI.createApi(false).then((api) => api.post<PartnerAddress>(`/admin/partner/${partnerId}/address`, address));
  }

  public static editPartnerAddress(partnerId: string, address: PartnerAddress): Promise<ApiResponse<PartnerAddress>> {
    return HealAPI.createApi(false).then((api) => api.put<PartnerAddress>(`/admin/partner/${partnerId}/address/${address.id}`, address));
  }

  public static deletePartnerAddress(partnerId: string, addressId: string): Promise<ApiResponse<ServerResponse>> {
    return HealAPI.createApi().then((api) => api.delete<ServerResponse>(`/admin/partner/${partnerId}/address/${addressId}`));
  }

  public static uploadImage(file: FormData): Promise<ApiResponse<PartnerLogo>> {
    return HealAPI.createApi(true, true).then((api) => api.post<PartnerLogo>('/admin/partner/logo', file));
  }

  public static fetchCurrentAccount(): Promise<ApiResponse<Account>> {
    return HealAPI.createApi().then((api) => api.get<Account>('/admin/accounts/me'));
  }

  public static fetchAccountInfo(accountId: string): Promise<ApiResponse<AccountResponse>> {
    return HealAPI.createApi().then((api) => api.get<AccountResponse>(`/admin/create-visit/${accountId}/info`));
  }

  public static fetchServiceOfferings(patientId: string, userAccountAddressId?: string, partnerAddressId?: string): Promise<ApiResponse<ServiceOffering[]>> {
    const data = partnerAddressId ? { partnerAddressId } : { userAccountAddressId };
    return HealAPI.createApi().then((api) => api.get<ServiceOffering[]>(`/admin/v4/create-visit/patient/${patientId}/service_offerings`, data));
  }

  public static fetchTimeSlots(
    serviceCode: string,
    patientId: string,
    userAccountAddressId?: string,
    partnerAddressId?: string,
    preferredDoctorOnly?: boolean
  ): Promise<ApiResponse<TimeSlotData[]>> {
    const data = partnerAddressId ? { partnerAddressId } : { preferredDoctorOnly, userAccountAddressId };
    return HealAPI.createApi().then((api) => api.get<TimeSlotData[]>(`/admin/create-visit/patient/${patientId}/service_offering/${serviceCode}/time_slots`, data));
  }

  public static saveVisit(data: any): Promise<ApiResponse<any>> {
    if (data.selectedDoctorId) {
      data = {
        ...data,
        selectedDoctorType: 'PREFERRED',
      };
    }
    return HealAPI.createApi(false).then((api) => api.post(`/admin/create-visit/user/${data.userAccountId}/patient/${data.patientId}/create-visit`, data));
  }

  public static async setConfirmationState(visitCode: string, state: ConfirmationStateType) {
    return (await HealAPI.createApi()).put<WebVisit>(`/admin/v1/visits/${visitCode}/confirmation-state`, { state });
  }

  public static async setWaitlisted(visitCode: string, waitlisted: boolean) {
    return (await HealAPI.createApi(false)).put<WebVisit>(`/admin/visits/${visitCode}/wait-list`, waitlisted);
  }

  public static async sendConfirmationCall(visitCode: string) {
    return (await HealAPI.createApi()).post<BaseResponse<boolean>>(`/switchboard/visits/${visitCode}/confirmation-call`);
  }

  public static async cancelUnconfirmedVisits(date: string, marketGroupCodes: string[]) {
    return (await HealAPI.createApi()).post<BaseResponse<boolean>>(`/admin/v1/visits/unconfirmed/cancel`, { date: moment(date).format('YYYY-MM-DD'), marketGroupCodes });
  }

  public static fetchClinicalNoteFlags(visitCode: string): Promise<ApiResponse<any>> {
    return HealAPI.createApi().then((api) => api.get(`/admin/visits/${visitCode}/careplanflags`));
  }

  public static updatePatientTags(patientId: string, updateNotesRequest: UpdatePatientTagsRequest): Promise<ApiResponse<Patient>> {
    return HealAPI.createApi(false).then((api) => api.put<Patient>(`/admin/patient/${patientId}/tags`, updateNotesRequest));
  }

  public static editVisitAddress(visitCode: string, address: Address): Promise<ApiResponse<EditAddressResponse>> {
    return HealAPI.createApi(false).then((api) =>
      api.put<EditAddressResponse>(`/admin/visits/${visitCode}/changeAddress`, {
        ...address,
        mode: 'NEW_ADDRESS',
      })
    );
  }

  public static addAddress(userAccountId: string, data: EditAddressRequest): Promise<ApiResponse<Address>> {
    return HealAPI.createApi(false).then((api) => api.post<Address>(`/admin/create-visit/${userAccountId}/create-address`, data));
  }

  public static editAddress(data: EditAddressRequest): Promise<ApiResponse<Address>> {
    return HealAPI.createApi().then((api) => api.put<Address>(`/admin/create-visit/address/${data.addressId}`, data));
  }

  public static addUserAccount(data: any): Promise<ApiResponse<Account>> {
    const accountData = {
      ...data,
      newUser: true,
      dateOfBirth: moment(data.dateOfBirth).format('YYYY-MM-DD'),
      inDoctorsOnCallPractice: false,
    };
    return HealAPI.createApi(false).then((api) => api.post<AccountResponse>('/admin/create-visit/create-account', accountData));
  }

  public static editUserAccount(userAccountId: string, data: any): Promise<ApiResponse<Account>> {
    return HealAPI.createApi(false).then((api) =>
      api.post<AccountResponse>(`/admin/create-visit/${userAccountId}/update-account`, {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        newUser: false,
        phoneNumber: data.phoneNumber,
        zipcode: data.zipcode,
      })
    );
  }

  public static fetchPatient(patientId: string): Promise<ApiResponse<any>> {
    return HealAPI.createApi().then((api) => api.get<any>(`/admin/patient/${patientId}`));
  }

  public static fetchPatientPreferredDoctor(patientId: string): any {
    return HealAPI.createApi().then((api) => api.get<any>(`/admin/patient/${patientId}/preferred-doctor`));
  }

  public static fetchAccountManagingAgencies(accountId: string) {
    return HealAPI.createApi(false).then((api) => api.get<BaseResponse<AgencyIdentity[]>>(`/admin/accounts/${accountId}/managing-agencies`));
  }

  public static setAccountManagingAgencies(userAccountId: string, agencyIds: string[]) {
    const conciergeAgencyIds: { [key: string]: [] } = {};
    agencyIds.map((a) => (conciergeAgencyIds[a] = []));
    return HealAPI.createApi(false).then((api) => api.put<any>(`/admin/accounts/${userAccountId}/managing-agencies`, { conciergeAgencyIds }));
  }

  public static createForm(patientId: string, request: CreateFormRequest) {
    return HealAPI.createFileManagerApi(false).then((api) => api.post<CreateFormResponse>(`/v1/${patientId}/forms`, request));
  }

  public static createFile({ patientId, contentType, extension, releaseToPatient, documentType, visitCode }: CreateFileParams) {
    // Creating an 'unreleased' file means a patient cannot see the file.
    const pathPartToSaveFile = releaseToPatient ? 'files' : 'unreleased';

    // Set true for non-PDF files. Sends file to Tesseract lambda that creates a PDF file with parsed text.
    const sendToTesseractLamba = extension !== 'pdf' ? 'true' : 'false'; // Requires string - known FileManager legacy issue

    return HealAPI.createFileManagerApi(false).then((api) =>
      api.post<CreateFileResponse>(`/v1/${patientId}/${pathPartToSaveFile}/`, {
        content_type: contentType,
        ocr: sendToTesseractLamba,
        extension,
        document_type: documentType,
        visit_code: visitCode,
        send_notification: releaseToPatient.toString(),
        released_at: releaseToPatient ? moment().toISOString() : undefined, // FileManager in the future may prevent updating this field
      })
    );
  }

  public static createPatientFileFolder(patient_id: string): Promise<ApiResponse<ServerResponse>> {
    return HealAPI.createFileManagerApi(false).then((api) => api.post<ServerResponse>(`/v1/`, { patient_id }));
  }

  public static releasePatientFile(patientId: string, fileId: string, visitCode: string, documentType: string, sendNotification: boolean) {
    const params = {
      convert_to: 'files',
      visit_code: visitCode,
      document_type: documentType,
      send_notification: sendNotification.toString(),
    };

    return HealAPI.createFileManagerApi(false).then((api) => api.post<PatientFileData>(`/v1/${patientId}/convert/unreleased/${fileId}`, undefined, { params: params }));
  }

  public static fetchPatientFiles(patientId: string, context: string, page?: number): Promise<ApiResponse<PatientFilesDataResponse>> {
    return HealAPI.createFileManagerApi().then((api) =>
      api.get<PatientFilesDataResponse>(`/v1/${patientId}/${context}`, {
        page,
        max_results: 100,
      })
    );
  }

  public static approvePatientFile(patientId: string, fileId: string, context: string, description?: string): Promise<ApiResponse<{ description: string }>> {
    return HealAPI.createApi(false).then((api) =>
      api.post<PatientFileResponse>(`/admin/patient/${patientId}/${context}/${fileId}/approve`, {
        description,
      })
    );
  }

  public static deletePatientFile(patientId: string, fileId: string, context: string) {
    return HealAPI.createFileManagerApi(false).then((api) => api.delete<boolean>(`/v1/${patientId}/${context}/${fileId}`));
  }

  public static patchFile(patientId: string, fileId: string, data: PatchFileRequest, context: string): Promise<ApiResponse<any>> {
    return HealAPI.createFileManagerApi(false).then((api) => api.patch<any>(`/v1/${patientId}/${context}/${fileId}`, data));
  }

  public static fetchPatientFile(patientId: string, fileId: string, context: string) {
    return HealAPI.createFileManagerApi().then((api) => api.get<PatientFileData>(`/v1/${patientId}/${context}/${fileId}`));
  }

  public static uploadFile(url: string, file: File): Promise<ApiResponse<any>> {
    return create({
      baseURL: '',
      headers: {
        'Content-Type': file.type,
      },
    }).put(url, file);
  }

  public static grantAccountOwnerAccessToPatientFiles(ownerAccountId: string, patientId: string) {
    return HealAPI.createFileManagerApi(false).then((api) => api.put<PatientFileGrantsResponse>(`/v1/${patientId}/grant/files`, { grants: 'download', userId: ownerAccountId }));
  }

  public static fetchPatientFilesGrants(patientId: string) {
    return HealAPI.createFileManagerApi(false).then((api) => api.get<PatientFileGrantsResponse>(`/v1/${patientId}/grants/files`));
  }

  public static fetchPCPFormData(patientId: string): Promise<ApiResponse<PCPFormData>> {
    return HealAPI.createApi(false).then((api) => api.get<PCPFormData>(`/admin/patient/${patientId}/pcpFormInfo`));
  }

  public static generateAndUploadPCPForm(patientId: string, providerId: string, request: PCPFormRequest) {
    return HealAPI.createApi(false).then((api) => api.post(`/admin/patient/${patientId}/prefilledPcpForm?providerId=${providerId}`, request));
  }

  public static setPCPStatus(patientId: string, request: Partial<PCPStatusChangeRequest>) {
    return HealAPI.createApi(false).then((api) => api.post(`/admin/patient/${patientId}/pcpStatusChange`, request));
  }

  public static setPatientPreferredDoctor(patientId: string, request: Partial<PreferredDoctorRequest>) {
    if (request.providerId) {
      return HealAPI.createApi(false).then((api) => api.post<any>(`/admin/patient/${patientId}/preferred-doctor`, request));
    } else {
      return HealAPI.createApi(false).then((api) => api.delete<any>(`/admin/patient/${patientId}/preferred-doctor`));
    }
  }

  public static addPatient(userAccountId: string, data: CreatePatientRequest): Promise<ApiResponse<CreatePatientResponse>> {
    const patientData = {
      ...data,
      dateOfBirth: moment(data.dateOfBirth).format('MM/DD/YYYY'),
    };
    return HealAPI.createApi(false).then((api) => api.post<CreatePatientResponse>(`/admin/create-visit/${userAccountId}/create-patient`, patientData));
  }

  public static editPatient(patientId: string, data: EditPatientRequest): Promise<ApiResponse<EditPatientResponse>> {
    const patientData = {
      ...data,
      dateOfBirth: moment(data.dateOfBirth).format('MM/DD/YYYY'),
    };
    return HealAPI.createApi(false).then((api) => api.put<EditPatientResponse>(`/admin/create-visit/edit-patient/${patientId}`, patientData));
  }

  public static markPatientAsDeceased(patientId: string, deceasedDate: Date) {
    const deceasedRequest = {
      deceasedDate: moment(deceasedDate).format('YYYY-MM-DD'),
    };
    return HealAPI.createApi(false).then((api) => api.put<Patient>(`/admin/patient/${patientId}/markAsDeceased`, deceasedRequest));
  }

  public static getPhoneNumberLineType(phoneNumber: string): Promise<ApiResponse<LineType>> {
    return HealAPI.createApi(false).then((api) => api.get<LineType>(`/admin/phoneutil/linetype/${phoneNumber}`));
  }

  public static getShiftsForDate(date: string): Promise<ApiResponse<DailyShiftSchedule>> {
    return HealAPI.createEtaApi().then((api) => api.get<DailyShiftSchedule>('/getShiftsForDate', { date: toISODateString(date) }));
  }

  public static setMAForShift(shiftId: string, maId: string): Promise<ApiResponse<Shift>> {
    return HealAPI.createEtaApi(true).then((api) => api.put<Shift>('/setMAForShift', { maId, shiftId }));
  }

  public static setNotesForShift(shiftId: string, notes: string): Promise<ApiResponse<Shift>> {
    return HealAPI.createEtaApi(true).then((api) => api.put<Shift>('/setNotesForShift', { notes, shiftId }));
  }

  public static setNotesForFloatMA(maId: string, notes: string, date: string, shiftType: string): Promise<ApiResponse<Shift>> {
    return HealAPI.createEtaApi(true).then((api) => api.put<Shift>('/setMANotes', { notes, maId, date, shiftType }));
  }

  public static sendFloatMessagesForDate(date: string, shiftType: string, maIds: string[]): Promise<ApiResponse<any>> {
    return HealAPI.createEtaApi().then((api) =>
      api.post<any>('/sendFloatMessagesForDate', {
        date: toISODateString(date),
        shiftType: shiftType,
        maIds: maIds,
      })
    );
  }

  public static sendMessagesForShifts(shiftIds: string[]): Promise<ApiResponse<Shift[]>> {
    return HealAPI.createEtaApi().then((api) => api.post<Shift[]>('/sendMessagesForShifts', { shiftIds: shiftIds }));
  }

  public static refreshShiftsForDate(date: string): Promise<ApiResponse<Shift[]>> {
    return HealAPI.createEtaApi().then((api) => api.post<Shift[]>('/refreshShiftsForDate', { date: toISODateString(date) }));
  }

  public static getPairingRules(): Promise<ApiResponse<ProviderPairingRule[]>> {
    return HealAPI.createEtaApi().then((api) => api.get<ProviderPairingRule[]>('/notAllowedMatches'));
  }

  public static insertPairingRules(providerId: string, providerIds: string[]): Promise<ApiResponse<ProviderPairingRule[]>> {
    return HealAPI.createEtaApi(true).then((api) => api.post<ProviderPairingRule[]>('/notAllowedMatches', { providerId, providerIds }));
  }

  public static removePairingRules(id: string, providerIds: string[]): Promise<ApiResponse<ProviderPairingRule[]>> {
    return HealAPI.createEtaApi(true).then((api) => api.put<ProviderPairingRule[]>(`/notAllowedMatches/${id}`, { providerIds }));
  }

  public static deleteProviderFromPairingRules(id: string): Promise<ApiResponse<void>> {
    return HealAPI.createEtaApi(true).then((api) => api.delete<void>(`/notAllowedMatches/${id}`));
  }

  public static findInsurancePlan(memberId: string, firstName: string, lastName: string, dob: string, payerCode: string): Promise<ApiResponse<InsurancePlanDocument[]>> {
    return HealAPI.createOpsApi().then((api) =>
      api.get<InsurancePlanDocument[]>(
        `/ips/v1/insuranceplan/q?memberFirstName=${firstName}&memberLastName=${lastName}&memberDob=${dob}&memberId=${memberId}&payerCode=${payerCode}`
      )
    );
  }

  public static getInsurancePlans(page?: number, size?: number): Promise<ApiResponse<any>> {
    return HealAPI.createOpsApi().then((api) => api.get('/ips/v1/insuranceplan', { page, size }));
  }

  public static createInsurancePlan(data: any): Promise<ApiResponse<any>> {
    return HealAPI.createOpsApi().then((api) => api.post('/ips/v1/insuranceplan', data));
  }

  private static handleCreateApi(baseURL: string, showDefaultMessage = true, multiPartRequest?: boolean): Promise<ApisauceInstance> {
    return getAccessToken().then((token: string) => {
      const apiSauceInstance = create({
        baseURL,
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': multiPartRequest ? 'multipart/form-data' : 'application/json',
        },
      });

      apiSauceInstance.axiosInstance.interceptors.response.use(
        (response) => {
          HealAPI.handleResponse(response, showDefaultMessage);
          return response;
        },
        (error) => {
          if (error.message === 'Network Error') {
            toastr.error(error.message, 'You may not be connected to the Internet - or the Heal VPN.');
          } else {
            HealAPI.handleErrorResponse(error.response || {}, showDefaultMessage);
          }
          return error;
        }
      );
      return apiSauceInstance;
    });
  }

  private static handleResponse(response: AxiosResponse<ServerResponse>, showDefaultMessage?: boolean) {
    if (response && response.data && response.data.description && response.data.status && response.data.status !== StatusOptions.OK) {
      toastr.error(response.data.status || '', response.data.description);
    } else if (response.config.method === 'post' || response.config.method === 'put') {
      if (showDefaultMessage) {
        const serverResponse = (response.data || {}) as ServerResponse;
        toastr.success(serverResponse.status || 'Success', serverResponse.description || '');
      }
    }
  }

  private static handleErrorResponse(response: AxiosResponse<ServerResponse>, showDefaultMessage?: boolean) {
    if (showDefaultMessage) {
      const serverResponse = (response.data || {}) as ServerResponse;
      toastr.error(serverResponse.status || 'Error', serverResponse.description || 'Something went wrong.');
    }
  }

  private static createApi(showDefaultMessage = true, multiPartRequest?: boolean): Promise<ApisauceInstance> {
    return HealAPI.handleCreateApi(healConfig.apiBaseUrl, showDefaultMessage, multiPartRequest);
  }

  private static createOpsApi(showDefaultMessage = true): Promise<ApisauceInstance> {
    return HealAPI.handleCreateApi(healConfig.opsApiBaseUrl, showDefaultMessage);
  }

  private static createAtlasApi(showDefaultMessage = false): Promise<ApisauceInstance> {
    return HealAPI.handleCreateApi(healConfig.atlasApiBaseUrl, showDefaultMessage);
  }

  private static createEtaApi(showDefaultMessage = false): Promise<ApisauceInstance> {
    return HealAPI.handleCreateApi(healConfig.etaApiBaseUrl, showDefaultMessage);
  }

  private static createFileManagerApi(showDefaultMessage = true): Promise<ApisauceInstance> {
    return HealAPI.handleCreateApi(healConfig.fileManagerApiBaseUrl, showDefaultMessage);
  }
}

export class VendorAPI {
  public static async fetchTimeZone(latitude: number, longitude: number): Promise<string> {
    const response = await fetch(
      'https://maps.googleapis.com/maps/api/timezone/json?location=' +
        latitude +
        ',' +
        longitude +
        '&timestamp=' +
        new Date().getTime() / 1000 +
        '&key=' +
        healConfig.googleMapsApiKey
    ).then((r) => r.json());
    return response.timeZoneId || 'America/Los_Angeles';
  }
}
